import * as React from 'react'

import { cn } from '@/lib/utils'

function Card({
  className,
  interactive = false,
  ...props
}: React.ComponentProps<'div'> & {
  /** Adds the DS hover elevation (Card `type=onHover`). Use for clickable cards. */
  interactive?: boolean
}) {
  return (
    <div
      data-slot="card"
      className={cn(
        // DS Card: white fill, borderless, and elevated with the Apple Store card shadow.
        'bg-card text-card-foreground gap-section py-card shadow-card flex flex-col rounded-xl text-base transition-shadow',
        interactive && 'hover:shadow-card-hover focus-within:shadow-card-hover',
        className,
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        // Headings (h1–h4) carry their own `mb-heading` for normal document flow, but
        // here the Card's own `gap-*` already spaces the header from CardContent —
        // reset that margin so the two don't stack into a doubled gap.
        'px-card [.border-b]:pb-card @container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [&_h1]:mb-0 [&_h2]:mb-0 [&_h3]:mb-0 [&_h4]:mb-0',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-base', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-card', className)} {...props} />
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('px-card [.border-t]:pt-card flex items-center', className)}
      {...props}
    />
  )
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent }
